// The SLOT GRID of a home widget body (HOME_WIDGETS_SPEC.md §2). A body root
// extends it — `CrmRecentWidgetBody: { extends: 'HomeWidgetBody', … }` — and
// fills it with the slot components (HomeWidgetHero, HomeWidgetDelta, one
// visual, HomeWidgetList, HomeWidgetFooter, HomeWidgetEmpty); each slot names
// its own `gridArea`, so a body never places anything itself.
//
// The areas follow the SIZE of the grid cell the widget sits in. The board
// stamps `data-widget-size="s|m|l|xl"` on every grid item (pages/main.js,
// functions/homeGridMetrics.js), and the size blocks below are ancestor
// selectors on it:
//   S  hero · delta · visual (a progress only) · footer
//   M  hero + delta left, the visual right, footer under both
//   L  hero · delta · visual · list (takes the rest) · footer
//   XL hero + delta | visual | list, footer under all three
// A slot the size does not show hides itself (display none) and an `auto`
// track collapses, so a body without a delta or a list leaves no hole. The
// footer's row takes the rest, and the footer pins itself to its bottom edge.
//
// WHY `:is([data-widget-size="m"]) &` and not a media token: the element layer
// compiles every registered `@token` to `@media <value>`, so a selector-valued
// token emits a dead `@media [data-widget-size…]` block (measured in jsdom,
// 2026-09-26). `:is()` also carries the attribute's specificity, (0,2,0), so a
// size block beats the base declaration by weight, never by insertion order —
// `:where()` would tie at (0,1,0).
//
// EMPTY: a body declares `empty` ONCE on its root — true when its data has
// answered and there is nothing to show — and this grid swaps every data slot
// for the one HomeWidgetEmpty sentence (spec §2: never "0", "0.00" or an
// empty tall card). While the data is unread `empty` stays false, so the
// slots keep their skeletons; the sentence never shows without the flag.
//
//   CrmRecentWidgetBody: {
//     extends: 'HomeWidgetBody',
//     empty: (el, s) => !!s.root.crmContactsLoaded && !(s.root.crmContacts || []).length,
//     …
//     HomeWidgetEmpty: { Text: { text: '{{ apps.widget.crmRecent.empty | polyglot }}' } }
//   }
//
// NARROW BOARDS: a size does not always get its width (spec §1: on one
// column every size is one cell wide, on two columns XL is two wide). The
// board stylesheet then gives the body the arrangement of the width it really
// spans (functions/homeGridMetrics.js `homeBodyFit` + `HOME_BODY_LAYOUTS`,
// pages/main.js `_injectWidgetStyles`). HOME_BODY_LAYOUTS is the same four
// blocks as below — a component cannot import it, so
// __tests__/homeSlotBodyArrangement.test.js pins the two copies equal.
// The `home-widget-body` class is what that sheet selects, and what keeps the
// two-tier bodies' flex filler off a slot grid.
//
// The frame owns the surface, padding and radius; this grid only lays out.
export const HomeWidgetBody = {
  class: 'home-widget-body',
  display: 'grid',
  flex: '1 1 auto',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateAreas: '"hero" "delta" "visual" "footer"',
  gridTemplateRows: 'auto auto auto minmax(0, 1fr)',
  rowGap: 'Z',
  columnGap: 'A',
  alignContent: 'stretch',
  height: '100%',
  minHeight: '0',
  minWidth: '0',
  empty: false,
  attr: { 'data-empty': (el) => (el.empty ? 'true' : null) },
  '&[data-empty] > :not(.home-widget-empty)': { display: 'none' },
  '&:not([data-empty]) > .home-widget-empty': { display: 'none' },

  ':is([data-widget-size="m"]) &': {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gridTemplateAreas: '"hero visual" "delta visual" "footer footer"',
    gridTemplateRows: 'auto minmax(0, 1fr) auto'
  },
  // L packs: every slot keeps its own height, the list takes the rest (its
  // rows at its top), the footer stays on the bottom edge — one band at most,
  // under the rows (crm-w1 twin QA F5: a face stack centred in a 2fr band and
  // rows in a 3fr band left three empty bands in a 280×572 card).
  ':is([data-widget-size="l"]) &': {
    gridTemplateAreas: '"hero" "delta" "visual" "list" "footer"',
    gridTemplateRows: 'auto auto auto minmax(0, 1fr) auto'
  },
  ':is([data-widget-size="xl"]) &': {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.25fr) minmax(0, 1.25fr)',
    gridTemplateAreas: '"hero visual list" "delta visual list" "footer footer footer"',
    gridTemplateRows: 'auto minmax(0, 1fr) auto'
  }
}
